// Detection for the benign Radix portal teardown race.
//
// Symptom in production: "DOMException NotFoundError: Failed to execute
// 'removeChild' on 'Node'" thrown from React's commit phase
// (commitDeletionEffectsOnFiber), which the page error boundary turned into the
// full "Something went wrong / Une erreur est survenue" screen.
//
// Cause: a Radix SelectPortal unmounting in the SAME commit as its parent
// Dialog. Both portals detach nodes from the same container, so React tries to
// remove a node the other portal already removed. The DOM still ends up in the
// intended state — nothing is left broken — so blanking the page is strictly
// worse than continuing to render.
//
// The real fix is ordering (close the dialog, refetch on the next tick). This
// predicate is the safety net for any portal combination we haven't hit yet.

// insertBefore is the same race seen from the other side: React tries to insert
// relative to a reference node a sibling portal already detached. Observed in
// production on /dashboard alongside the removeChild variant.
const REMOVE_CHILD_RE =
  /removeChild|insertBefore|The object can not be found here/i;

/** True only for the known-benign portal-teardown DOM race. */
export function isPortalTeardownRace(error: unknown): boolean {
  // DOMException is not available in every environment (SSR/node), so feature
  // detect rather than relying on `instanceof` alone.
  const isDomException =
    typeof DOMException !== "undefined" && error instanceof DOMException;

  if (isDomException) {
    const err = error as DOMException;
    return err.name === "NotFoundError" && REMOVE_CHILD_RE.test(err.message);
  }

  // Some browsers surface it as a plain Error with the name preserved.
  if (error instanceof Error) {
    return error.name === "NotFoundError" && REMOVE_CHILD_RE.test(error.message);
  }

  return false;
}
